import {
  transformValueType,
  transformValueTypeForServer,
} from '@data/value-type'
import { isPortalLogin } from '@utils/auth'
import { getEncryptedPassword, getDecryptedPassword } from '@utils/password'

const PermissionMap = {
  can_manage_service_catalog: 'admin.service_catalog',
  can_manage_request_management: 'admin.request_management',
  can_manage_problem_management: 'admin.problem_management',
  can_manage_change_management: 'admin.change_management',
  can_manage_release_management: 'admin.release_management',
  can_manage_organization: 'admin.organization',
  can_manage_ai: 'admin.ai',
  /* Organization chield parmissions - start */
  can_manage_account: 'admin.organization.account',
  can_manage_branding: 'admin.organization.branding',
  can_manage_departments: 'admin.organization.departments',
  can_manage_locations: 'admin.organization.locations',
  can_manage_business_hours: 'admin.organization.business_hours',
  can_manage_announcements: 'admin.organization.announcements',
  can_manage_business_service: 'admin.organization.business_service',
  can_manage_leave_management: 'admin.organization.leave_management',
  can_manage_system_preference: 'admin.organization.system_preference',
  can_manage_security: 'admin.organization.security',
  can_manage_priority: 'admin.organization.priority',
  can_manage_impact: 'admin.organization.impact',
  can_manage_urgency: 'admin.organization.urgency',
  can_manage_application_maintenance:
    'admin.organization.application_maintenance',
  can_manage_proxy_server_configuration:
    'admin.organization.proxy_server_configuration',
  can_manage_managed_services_providers:
    'admin.organization.managed_services_providers',
  can_manage_tenant_management:
    'admin.organization.can_manage_tenant_management',
  can_manage_privacy_setting: 'admin.organization.manage_privacy_setting',
  /* Organization chield parmissions - end */
  can_manage_support_channels: 'admin.support_channels',
  can_manage_automation: 'admin.automation',
  can_manage_knowledge: 'admin.knowledge_management',
  can_manage_review: 'knowledge.can_manage_review',
  /** **************** User Permission start ******************/
  can_manage_users: 'admin.users',
  can_view_users: 'admin.user.view',
  can_create_user: 'admin.user.create',
  can_update_user: 'admin.user.update',
  can_verify_user: 'admin.user.verify_user',
  can_block_unblock_user: 'admin.user.block_unblock_user',
  can_unlock_user: 'admin.user.unlock_user',
  can_convert_user: 'admin.user.convert_user',
  can_set_password: 'admin.user.set_password',
  can_archive_user: 'admin.user.archive_user',
  can_clear_user: 'admin.user.clear_user',
  can_restore_user: 'admin.user.restore_user',
  can_anonymize_user: 'admin.user.anonymize_user',
  can_manage_users_custom_field: 'admin.user.custom_field',
  can_manage_users_groups: 'admin.user.users_groups',
  can_manage_users_roles: 'admin.user.users_roles',
  can_manage_ldap_users: 'admin.user.users_ldap',
  can_manage_sso_users: 'admin.user.users_sso',
  can_manage_custom_scope: 'admin.user.custom_scope',
  /** **************** User Permission end ******************/
  can_manage_asset_management: 'admin.asset_management',
  can_manage_contract: 'admin.contract_management',
  can_manage_asset_discovery: 'admin.asset_discovery',
  can_manage_agents: 'admin.agent_management',
  can_manage_catalogs: 'admin.asset_catalogs',
  can_manage_purchase: 'admin.purchase_management',
  can_manage_user_survey: 'admin.user_survey_management',
  can_manage_project_management: 'admin.project_management',
  project_can_manage_members: 'project.manage_members',
  can_manage_patch_management: 'admin.patch_management',
  can_manage_deployment_management: 'admin.deployment',
  hardware_software_asset: 'asset.hardware_software_asset',
  non_it_asset: 'asset.non_it_asset',
  consumable_asset: 'asset.consumable_asset',
  software_license_can_view: 'asset.software_license_can_view',
  can_view_geo_location: 'asset.can_view_geo_location',
  can_view_dashboard: 'dashboard.dashboard_can_view',
  can_manage_dashboard: 'dashboard.manage',
  can_manage_task_management: 'admin.task_management',
  request_can_create_requester: 'request.can_create_requester',
  /** *** start cmdb permissions ****/
  can_manage_geo_location_cmdb: 'cmdb.manage_geo_location_cmdb',
  can_view_geo_location_cmdb: 'cmdb.view_geo_location_cmdb',
  can_manage_attachments_cmdb: 'cmdb.manage_attachments_cmdb',
  can_manage_linked_events_cmdb: 'cmdb.manage_linked_events_cmdb',
  can_archive_cmdb: 'cmdb.archive_cmdb',
  can_create_cmdb: 'cmdb.create_cmdb',
  can_update_cmdb: 'cmdb.update_cmdb',
  can_manage_cmdb: 'admin.cmdb_management',
  can_manage_relationships_cmdb: 'cmdb.manage_relationships_cmdb',
  can_manage_tags_cmdb: 'cmdb.manage_tags_cmdb',
  can_view_cmdb: 'cmdb.view_cmdb',
  /** *** end cmdb permissions ****/
  request_can_manage_solution: 'request.request_can_manage_solution',
  request_can_update_due_by: 'request.can_update_due_by',
  problem_can_update_due_by: 'problem.can_update_due_by',
  change_can_update_due_by: 'change.can_update_due_by',
  release_can_update_due_by: 'release.can_update_due_by',

  /** *** start approval permissions ****/
  // Request approval permissions
  request_can_create_approval: 'request.create.approval',
  request_can_ignore_approval: 'request.ignore.approval',
  request_can_delete_approval: 'request.delete.approval',

  // Problem approval permissions
  problem_can_create_approval: 'problem.create.approval',
  problem_can_ignore_approval: 'problem.ignore.approval',
  problem_can_delete_approval: 'problem.delete.approval',

  // Change approval permissions
  change_can_create_approval: 'change.create.approval',
  change_can_ignore_approval: 'change.ignore.approval',
  change_can_delete_approval: 'change.delete.approval',

  // Release approval permissions
  release_can_create_approval: 'release.create.approval',
  release_can_ignore_approval: 'release.ignore.approval',
  release_can_delete_approval: 'release.delete.approval',

  // CMDB approval permissions
  can_create_approval_cmdb: 'cmdb.create.approval',
  can_ignore_approval_cmdb: 'cmdb.ignore.approval',
  can_delete_approval_cmdb: 'cmdb.delete.approval',

  // Hardware asset approval permissions
  can_create_approval_hardware_software_asset: 'asset_hardware.create.approval',
  can_ignore_approval_hardware_software_asset: 'asset_hardware.ignore.approval',
  can_delete_approval_hardware_software_asset: 'asset_hardware.delete.approval',

  // Non-IT asset approval permissions
  can_create_approval_nonit_asset: 'asset_non_it.create.approval',
  can_ignore_approval_nonit_asset: 'asset_non_it.ignore.approval',
  can_delete_approval_nonit_asset: 'asset_non_it.delete.approval',

  // Purchase approval permissions
  can_create_approval_purchase: 'purchase.create.approval',
  can_ignore_approval_purchase: 'purchase.ignore.approval',
  can_delete_approval_purchase: 'purchase.delete.approval',

  // Knowledge approval permissions
  can_create_approval_knowledge: 'knowledge.create.approval',
  can_ignore_approval_knowledge: 'knowledge.ignore.approval',
  can_delete_approval_knowledge: 'knowledge.delete.approval',
  /** *** end approval permissions ****/

  /** **************** Task Permissions start ******************/
  task_can_view: 'task.view',
  task_can_create: 'task.create',
  task_can_update: 'task.update',
  task_can_assign: 'task.assign',
  task_can_update_priority: 'task.update_priority',
  task_can_update_status: 'task.update_status',
  task_can_resolve: 'task.resolve',
  task_can_close: 'task.close',
  task_can_reopen: 'task.reopen',
  task_can_archive: 'task.archive',

  // Request Task Permissions
  request_task_can_view: 'request.task.view',
  request_task_can_create: 'request.task.create',
  request_task_can_update: 'request.task.update',
  request_task_can_update_priority: 'request.task.update_priority',
  request_task_can_update_status: 'request.task.update_status',
  request_task_can_resolve: 'request.task.resolve',
  request_task_can_close: 'request.task.close',
  request_task_can_reopen: 'request.task.reopen',
  request_task_can_archive: 'request.task.archive',
  request_task_can_assign: 'request.task.assign',

  // Problem Task Permissions
  problem_task_can_view: 'problem.task.view',
  problem_task_can_create: 'problem.task.create',
  problem_task_can_update: 'problem.task.update',
  problem_task_can_update_priority: 'problem.task.update_priority',
  problem_task_can_update_status: 'problem.task.update_status',
  problem_task_can_resolve: 'problem.task.resolve',
  problem_task_can_close: 'problem.task.close',
  problem_task_can_reopen: 'problem.task.reopen',
  problem_task_can_archive: 'problem.task.archive',
  problem_task_can_assign: 'problem.task.assign',
  // Change Task Permissions
  change_task_can_view: 'change.task.view',
  change_task_can_create: 'change.task.create',
  change_task_can_update: 'change.task.update',
  change_task_can_update_priority: 'change.task.update_priority',
  change_task_can_update_status: 'change.task.update_status',
  change_task_can_resolve: 'change.task.resolve',
  change_task_can_close: 'change.task.close',
  change_task_can_reopen: 'change.task.reopen',
  change_task_can_archive: 'change.task.archive',
  change_task_can_assign: 'change.task.assign',
  // Release Task Permissions
  release_task_can_view: 'release.task.view',
  release_task_can_create: 'release.task.create',
  release_task_can_update: 'release.task.update',
  release_task_can_update_priority: 'release.task.update_priority',
  release_task_can_update_status: 'release.task.update_status',
  release_task_can_resolve: 'release.task.resolve',
  release_task_can_close: 'release.task.close',
  release_task_can_reopen: 'release.task.reopen',
  release_task_can_archive: 'release.task.archive',
  release_task_can_assign: 'release.task.assign',
  // Project Task Permissions
  project_can_view_planning: 'project.task.view',
  project_task_can_create: 'project.task.create',
  project_task_can_update: 'project.task.update',
  project_task_can_update_priority: 'project.task.update_priority',
  project_task_can_update_status: 'project.task.update_status',
  project_task_can_resolve: 'project.task.resolve',
  project_task_can_close: 'project.task.close',
  project_task_can_reopen: 'project.task.reopen',
  project_task_can_archive: 'project.task.archive',
  project_task_can_assign: 'project.task.assign',
  // Standalone Task Permissions
  standalone_task_can_view: 'task.view',
  standalone_task_can_create: 'task.create',
  standalone_task_can_update: 'task.update',
  standalone_task_can_update_priority: 'task.update_priority',
  standalone_task_can_update_status: 'task.update_status',
  standalone_task_can_resolve: 'task.resolve',
  standalone_task_can_close: 'task.close',
  standalone_task_can_reopen: 'task.reopen',
  standalone_task_can_archive: 'task.archive',
  standalone_task_can_assign: 'task.assign',
  /** **************** Task Permissions end ******************/
}

function transformAllowedModulePermissions(modules = {}) {
  const allowedModules = Object.keys(modules)
  Object.keys(modules).forEach((key) => {
    modules[key].forEach((permissionObj) => {
      if (PermissionMap[permissionObj.permission]) {
        allowedModules.push(PermissionMap[permissionObj.permission])
      }
    })
  })
  return allowedModules
}

export function transformPreference(preference) {
  const modulePreferences = {}
  let p = preference.moduleUserPreferences || []
  p.forEach((p) => {
    modulePreferences[p.model] = transformModulePreference(p)
  })
  return {
    modulePreferences,
    dashboardPreferenceForUser: preference.dashboardPreferenceForUser,
    pageSize: preference.globalDefaultPageSize,
    theme: preference.theme,
    isSetupCompleted: preference.isSetupCompleted,
    shortcutEnabled: !preference.shortcutDisabled,
    showUiNotification: preference.showUiNotification,
    webNotificationPrefs: preference.webNotificationPrefs,
    supportChat: preference.supportChat,
    myAllowedModules: transformAllowedModulePermissions(
      isPortalLogin()
        ? preference.supportedRolesModuleForRequester
        : preference.supportedRolesModule
    ),
    attachmentPassword: preference.attachmentPassword
      ? getDecryptedPassword(preference.attachmentPassword)
      : '',
  }
}

export function transformModulePreference(modulePreference) {
  return {
    columns: modulePreference.columns,
    columnsForSupportPortal: modulePreference.columnsForSupportPortal,
    kanbanStatusColumns: modulePreference.kanbanStatusColumns,
    defaultLayout: modulePreference.defaultPageLayout === 0 ? 'list' : 'split',
    pageSize: modulePreference.defaultPageSize,
    defaultSearchId: modulePreference.defaultQualification,
    moduleName: modulePreference.model,
    favouriteItems: modulePreference.favouriteItems,
    autoRefreshIntervalTime: modulePreference.autoRefreshIntervalTime,
  }
}

export function transformPreferenceForServer(preference) {
  return {
    moduleUserPreferences: Object.keys(preference.modulePreferences).map((k) =>
      transformModulePreferenceForServer(preference.modulePreferences[k])
    ),
    globalDefaultPageSize: preference.pageSize,
    shortcutDisabled: !preference.shortcutEnabled,
    showUiNotification: preference.showUiNotification,
    webNotificationPrefs: preference.webNotificationPrefs,
    theme: preference.theme,
    isSetupCompleted: preference.isSetupCompleted,
    dashboardPreferenceForUser: preference.dashboardPreferenceForUser,
    attachmentPassword: preference.attachmentPassword
      ? getEncryptedPassword(preference.attachmentPassword)
      : '',
  }
}

export function transformModulePreferenceForServer(preference) {
  return {
    columns: preference.columns,
    columnsForSupportPortal: preference.columnsForSupportPortal,
    kanbanStatusColumns: preference.kanbanStatusColumns,
    defaultPageSize: preference.pageSize,
    defaultQualification: preference.defaultSearchId,
    defaultPageLayout: preference.defaultLayout === 'list' ? 0 : 1,
    model: preference.moduleName,
    theme: preference.theme,
    favouriteItems: preference.favouriteItems,
    autoRefreshIntervalTime: preference.autoRefreshIntervalTime,
  }
}

export const transformTenantPreference = (preferences) => {
  const filteredPreference = preferences.preferences || []
  const map = {}
  filteredPreference.forEach((preference) => {
    map[preference.propertyKey] = {
      propertyKey: preference.propertyKey,
      ...transformValueType(preference.valueRest),
    }
  })
  map.showPassword = preferences.userActivationMethod === 'password'
  map.qrCodeSupport = preferences.qrCodeSupport
  map.enableAutoUnistalltionSoftware =
    preferences.enableAutoUnistalltionSoftware
  map.qrCodeMandatoryPropKeys = preferences.qrCodeMandatoryPropKeys
  map.qrCodeCustomfieldKeys = preferences.qrCodeCustomfieldKeys
  return map
}

export const transformTenantPreferenceForServer = (preference) => ({
  preferences: [
    {
      propertyKey: preference.propertyKey,
      valueRest: transformValueTypeForServer(
        preference.valueType,
        preference.value
      ).value,
    },
  ],
})
